import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useConfirmDialog } from "contexts/confirmDialog";
import { useTenantTranslation } from "hooks/formatters";
import { usePermission } from "hooks/permissions";
import { useTenant } from "hooks/settings";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useState } from "react";
import {
  HistoricalHeatOverride,
  HistoricalHeatOverrideVersion,
  useCancelHistoricalHeatOverrideMutation,
} from "src/store/api/generatedApi";
import { ChargeDeltaList } from "../delta/ChargeDeltaList";
import { EditHistoricalOverrideDialog } from "./EditHistoricalOverrideDialog";
import {
  formatOverrideReason,
  OverrideComment,
  OverrideMixComparison,
} from "./OverrideDetailSections";

export const HistoricalOverrideDialog = ({
  override,
  onClose,
  onSaved,
}: {
  override: HistoricalHeatOverride;
  onClose: () => void;
  onSaved: (override: HistoricalHeatOverride) => void;
}) => {
  const { t } = useTenantTranslation();
  const { toLocalDatetime } = useLocalDatetime();
  const confirm = useConfirmDialog();
  const canEditHistory = usePermission(["edit_override_history"]);
  const [editing, setEditing] = useState(false);
  const tenantName = useTenant();
  const [cancelOverride, { isLoading: isRemoving, isError: removeFailed }] =
    useCancelHistoricalHeatOverrideMutation();
  const remove = async () => {
    if (
      !(await confirm({
        title: t("removeHistoricalOverrideTitle"),
        message: t("removeHistoricalOverrideMessage"),
      }))
    )
      return;
    const cancellation = await cancelOverride({
      tenantName,
      overrideId: override.override_id,
    });
    if (!("error" in cancellation)) onClose();
  };
  const versions = override.versions;
  const latest = versions.at(-1);
  if (editing) {
    return (
      <EditHistoricalOverrideDialog
        override={override}
        onClose={() => {
          setEditing(false);
        }}
        onSaved={(saved) => {
          setEditing(false);
          onSaved(saved);
        }}
      />
    );
  }
  return (
    <Dialog
      open
      onClose={isRemoving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="historical-override-title"
    >
      <DialogTitle id="historical-override-title">
        {t("overrideDetails")}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2 }}>
          {override.cancelled_at !== null && (
            <Alert severity="info">
              {t("overrideCancelledDetail", {
                time: toLocalDatetime(override.cancelled_at),
                name: override.cancelled_by ?? "—",
              })}
            </Alert>
          )}
          <Stack sx={{ gap: 1 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("overrideHeatReferences")}
            </Typography>
            {override.scopes.map((scope) => (
              <Typography
                key={scope.stable_heat_id}
                variant="body2"
                sx={{ overflowWrap: "anywhere" }}
              >
                {scope.stable_heat_id} ({scope.expected_grade})
              </Typography>
            ))}
          </Stack>
          {override.charge_deltas !== null ? (
            <ChargeDeltaList chargeDeltas={override.charge_deltas} />
          ) : (
            <OverrideMixComparison
              replacedMix={override.replaced_mix}
              overrideMix={override.override_mix}
              recipeName={override.recipe_name}
            />
          )}
          <Typography variant="body2">
            {formatOverrideReason(override)}
          </Typography>
          <OverrideComment comment={override.comment} />
          {removeFailed && (
            <Alert severity="error">{t("cancelOverrideError")}</Alert>
          )}
          {canEditHistory && override.editability === "unfinished" && (
            <Alert severity="info">
              {t("historicalOverrideUnfinishedHint")}
            </Alert>
          )}
          {latest?.revision_id != null && (
            <>
              <VersionHeading version={latest} />
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {t("viewChanges")}
                </AccordionSummary>
                <AccordionDetails>
                  <Stack sx={{ gap: 2 }}>
                    {[...versions].reverse().map((version) => (
                      <Stack
                        key={version.revision_id ?? "original"}
                        sx={{ gap: 1 }}
                      >
                        <VersionHeading version={version} />
                        <OverrideMixComparison
                          replacedMix={override.replaced_mix}
                          overrideMix={version.override_mix}
                          recipeName={version.recipe_name}
                        />
                        <Typography variant="body2">
                          {formatOverrideReason(version)}
                        </Typography>
                        <OverrideComment comment={version.comment} />
                      </Stack>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {canEditHistory && override.editability === "available" && (
          <>
            <Button
              disabled={isRemoving}
              onClick={() => {
                setEditing(true);
              }}
            >
              {t("edit")}
            </Button>
            <Button
              color="error"
              loading={isRemoving}
              onClick={() => {
                void remove();
              }}
            >
              {t("remove")}
            </Button>
          </>
        )}
        <Button disabled={isRemoving} onClick={onClose}>
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VersionHeading = ({
  version,
}: {
  version: HistoricalHeatOverrideVersion;
}) => {
  const { t } = useTenantTranslation();
  const { toLocalDatetime } = useLocalDatetime();
  return (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {t(
        version.revision_id === null
          ? "historicalOverrideOriginalBy"
          : "historicalOverrideEditedBy",
        {
          name: version.edited_by,
          time: toLocalDatetime(version.edited_at),
        }
      )}
    </Typography>
  );
};
